import type { CompanyFilter as LegacyCompanyFilter } from "@/legacy/types/companyFilter";

type JsonObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is JsonObject =>
  !!value && typeof value === "object" && !Array.isArray(value);

// OppGen's DEFAULT_INCLUSION_FILTER (minus the UI-only isBulk).
const DEFAULT_INCLUSION_FILTER = {
  ids: [],
  roleIds: [],
  departmentIds: [],
  installAge: null,
  installAgeMonths: null,
  inclusionMethod: "ANY_PRESENT",
};

/**
 * Key order and unset values of OppGen's `translateOppGenFiltersToCompanyFilter`
 * (piq-customer-ui `utils/spog/opgen.ts`), which every OppGen Universe JSON
 * carries. `undefined` marks keys OppGen omits while unset. covered-wagon
 * relies on some of these empty values: in Filters mode it maps
 * `intent.contextTypeIds` and `intent.buyersJourneyIds` and raises on nil.
 */
const OPPGEN_FILTER_DEFAULTS: JsonObject = {
  productAttributes: { ids: [], installAge: null, installAgeMonths: null },
  companyMatch: null,
  hgIds: null,
  hgIdsIncludingCorporateRelatives: null,
  fuzzySearch: null,
  industry: null,
  revenue: undefined,
  employees: undefined,
  fitScore: undefined,
  intentScore: undefined,
  hasFixedEmployees: undefined,
  hasFixedRevenue: undefined,
  countryAlpha2s: null,
  regionNames: null,
  stateIds: null,
  excludedCountryAlpha2s: null,
  globalPresence: {
    isMultiNational: false,
    subsidiaryLocations: { inclusionMethod: "ANY_PRESENT" },
  },
  isGu: null,
  isDu: null,
  isGhq: null,
  isGp: null,
  isDp: null,
  intent: {
    topics: { ids: [], inclusionMethod: "ANY_PRESENT" },
    buyersJourneyIds: [],
    signalScore: "HIGH",
    contextTypeIds: [],
    location: {
      countryAlpha2s: null,
      regionNames: null,
      stateIds: null,
      excludedCountryAlpha2s: null,
    },
    numberOfCadences: null,
  },
  vendors: [DEFAULT_INCLUSION_FILTER],
  products: [DEFAULT_INCLUSION_FILTER],
  productCategories: {
    ids: [],
    roleIds: [],
    departmentIds: [],
    installAge: null,
    installAgeMonths: null,
  },
  spend: undefined,
  whitespace: null,
  knownCompanies: undefined,
  departmentIds: undefined,
  roleIds: undefined,
  includeDecisionMakersAndInfluencersOnly: undefined,
  buyerIntentProducts: undefined,
  buyerIntentVendors: undefined,
  buyerIntentCategories: undefined,
  buyerIntentAttributes: undefined,
  contactFilters: undefined,
};

// A set value replaces the default; nested objects merge key by key in the
// default's order; an empty array or a missing value keeps the default.
function mergeOntoDefault(defaultValue: unknown, value: unknown): unknown {
  if (value === undefined || value === null) return defaultValue;
  if (Array.isArray(value) && value.length === 0) return defaultValue;
  if (isPlainObject(defaultValue) && isPlainObject(value)) {
    const merged: JsonObject = {};
    Object.keys(defaultValue).forEach((key) => {
      const next = mergeOntoDefault(defaultValue[key], value[key]);
      if (next !== undefined) merged[key] = next;
    });
    Object.keys(value).forEach((key) => {
      if (!(key in defaultValue)) merged[key] = value[key];
    });
    return merged;
  }
  return value;
}

/**
 * MA's company filters in the shape OppGen sends them in the Universe JSON:
 * OppGen's key order, every key OppGen always sends at its empty value, and
 * MA's set values on top.
 */
export function toOppGenFilterShape(
  filters: LegacyCompanyFilter
): LegacyCompanyFilter {
  const shaped = mergeOntoDefault(
    OPPGEN_FILTER_DEFAULTS,
    filters
  ) as JsonObject;

  // OppGen's validateIntentFilter drops numberOfCadences once topics are set
  // and it is empty.
  const intent = shaped.intent as JsonObject;
  const topicIds = (intent.topics as JsonObject | undefined)?.ids;
  if (
    Array.isArray(topicIds) &&
    topicIds.length > 0 &&
    intent.numberOfCadences == null
  ) {
    const { numberOfCadences: _numberOfCadences, ...rest } = intent;
    shaped.intent = rest;
  }

  return shaped as unknown as LegacyCompanyFilter;
}
